// src/components/common/vibe/VoiceToHRDashboard.js
import React, { useState, useEffect } from 'react';
import { MessageSquare, RefreshCw, User, Calendar, ExternalLink } from 'lucide-react';
import vibeService from '../../../services/vibe.service';
import VoiceToHRFullDashboard from './VoiceToHRFullDashboard';

const STATUS_COLORS = {
    pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800',
    reviewed: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    in_progress: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800',
    closed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800'
};

const CATEGORY_LABELS = {
    fun_idea: '🎉 Fun Idea',
    appreciation: '💝 Appreciation',
    improvement: '💡 Improvement',
    concern: '⚠️ Concern'
};

const VoiceToHRDashboard = ({ theme }) => {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [showFullDashboard, setShowFullDashboard] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState(null);

    const fetchSubmissions = async () => {
        setLoading(true);
        try {
            //console.log('🔄 Fetching Voice to HR submissions...');
            const response = await vibeService.getSuggestionsDashboard();
            // //console.log('📋 Voice to HR API Response:', {
            //     success: response.success,
            //     dataCount: response.data?.length || 0,
            //     data: response.data
            // });
            setSubmissions(response.data || []);
            //console.log('✅ Submissions set:', response.data?.length || 0, 'items');
        } catch (error) {
            console.error('❌ Error fetching Voice to HR submissions:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubmissions();
    }, []);

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchSubmissions();
        setRefreshing(false);
    };

    const formatStatus = (status) => {
        return status?.replace('_', ' ').toUpperCase() || 'PENDING';
    };

    if (loading) {
        return (
            <div className={`rounded-xl border p-6 ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <div className="flex items-center justify-center py-8">
                    <RefreshCw className={`w-8 h-8 animate-spin ${
                        theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                    }`} />
                </div>
            </div>
        );
    }

    return (
        <div className={`rounded-xl border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
            {/* Header */}
            <div className={`p-4 border-b flex items-center justify-between ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                <div className="flex items-center gap-2">
                    <MessageSquare className={`w-5 h-5 ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <h3 className={`font-semibold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        Voice to HR
                    </h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                    }`}>
                        {submissions.length}
                    </span>
                </div>
                <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className={`p-1.5 rounded-lg transition-colors ${
                        theme === 'dark'
                            ? 'hover:bg-gray-700 text-gray-400'
                            : 'hover:bg-gray-100 text-gray-600'
                    } ${refreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                {submissions.length === 0 ? (
                    <div className="p-8 text-center">
                        <MessageSquare className={`w-12 h-12 mx-auto mb-3 ${
                            theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                        }`} />
                        <p className={`text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            No submissions yet
                        </p>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead>
                            <tr className={`border-b ${
                                theme === 'dark' ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'
                            }`}>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Employee
                                </th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Category
                                </th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Message
                                </th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Status
                                </th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Date
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {submissions.map((submission, index) => (
                                <tr
                                    key={submission.id}
                                    className={`border-b ${
                                        theme === 'dark' ? 'border-gray-700 hover:bg-gray-750' : 'border-gray-200 hover:bg-gray-50'
                                    } transition-colors cursor-pointer`}
                                    onClick={() => {
                                        setSelectedSubmission(submission);
                                        setShowFullDashboard(true);
                                    }}
                                >
                                    <td className={`px-4 py-3 text-sm ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        <div className="flex items-center gap-2">
                                            <User className="w-4 h-4" />
                                            {submission.is_anonymous ? (
                                                <span className="italic text-gray-500">Anonymous</span>
                                            ) : (
                                                <span>{submission.author_name || 'Unknown'}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className={`px-4 py-3 text-xs ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                        {CATEGORY_LABELS[submission.suggestion_category] || submission.suggestion_category}
                                    </td>
                                    <td className={`px-4 py-3 text-sm ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        <div className="max-w-xs truncate">
                                            {submission.content}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                                            STATUS_COLORS[submission.suggestion_status] || STATUS_COLORS.pending
                                        }`}>
                                            {formatStatus(submission.suggestion_status)}
                                        </span>
                                    </td>
                                    <td className={`px-4 py-3 text-xs ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                        <div className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {new Date(submission.created_at).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                timeZone: 'Asia/Kolkata'
                                            })}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* View All Link */}
            {submissions.length > 0 && (
                <div className={`p-3 border-t text-center ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <button
                        onClick={() => setShowFullDashboard(true)}
                        className={`flex items-center gap-2 mx-auto text-sm font-medium transition-colors ${
                            theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                        }`}
                    >
                        View All Submissions
                        <ExternalLink className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Full Dashboard Modal */}
            <VoiceToHRFullDashboard
                isOpen={showFullDashboard}
                onClose={() => {
                    setShowFullDashboard(false);
                    setSelectedSubmission(null);
                }}
                selectedSubmission={selectedSubmission}
                theme={theme}
            />
        </div>
    );
};

export default VoiceToHRDashboard;
